import { GenericForm } from '@neo/blocks/forms/generic-form'
import type { CustomCompType } from '@neo/blocks/forms/types'
import { Button, ButtonWithLoader } from '@neo/index'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@neo/primitives/dialog'
import { useMemo } from 'react'
import { queryClient } from 'v2/configs/rq-setup'
import { MultiFlowSelect } from 'v2/shared/components/flow-select'
import { flowQueryKeys } from 'v2/shared/utils/query-key-management'
import z from 'zod'
import { documentSetupSchema } from './config'
import { SyncFrequencySelect } from './new/document-setup.config'
import { useKBMutations } from './queries'
import type { SyncFrequencyPeriod } from './types'

interface RenameKBDialogProps {
  open: boolean
  setOpen: (open: boolean) => void
  name: string
  phlo_uuids: string[]
  id: string
  include_youtube_content?: boolean
  include_image_content?: boolean
  sync_frequency: Record<string, unknown>
  sync_enabled: boolean
  // The agent-link reverse-read (PHLO-core) failed, so the incoming `phlo_uuids` is non-authoritative
  // (coalesced to []). This dialog's "Associated Agent Flows" field would seed empty and a Save would
  // full-replace knowledge_base_ids → silently drop the KB's existing agent links. Disable Save.
  linksUnavailable?: boolean
}
interface RenameKBFormValues {
  name: string
  phlo_uuids: string[]
  ignore_youtube_content: boolean
  ignore_image_content: boolean
  sync_frequency: Record<string, unknown>
  sync_enabled: boolean
}
export const RenameKBDialog = ({
  open,
  setOpen,
  name,
  phlo_uuids,
  id,
  include_youtube_content = false,
  include_image_content = false,
  sync_frequency,
  sync_enabled,
  linksUnavailable = false,
}: RenameKBDialogProps) => {
  const { edit } = useKBMutations()
  const defaultValues: RenameKBFormValues = useMemo(
    () => ({
      name,
      phlo_uuids,
      ignore_youtube_content: !include_youtube_content,
      ignore_image_content: !include_image_content,
      sync_frequency,
      sync_enabled,
    }),
    [
      name,
      phlo_uuids,
      include_youtube_content,
      include_image_content,
      sync_frequency,
      sync_enabled,
    ],
  )
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Edit Knowledge Base</DialogTitle>
        </DialogHeader>
        <GenericForm
          fields={[
            {
              name: 'name',
              label: 'Knowledge base name',
              componentType: 'input',
              zodValidation: z.string().min(1, 'Name is required'),
            },
            {
              name: 'phlo_uuids',
              label: 'Associated Agent Flows',
              type: 'custom',
              component: MultiFlowSelect as CustomCompType<RenameKBFormValues>,
              eventDataExtractor: (e) => e as string[],
              zodValidation: z.array(z.string()),
              inputProps: { 'data-testid': 'select-kb-agents' },
            },
            {
              name: 'ignore_youtube_content',
              className: 'mt-4',
              componentType: 'checkbox',
              zodValidation: z.boolean().optional(),
              inputProps: {
                label: (
                  <div className="flex flex-col">
                    <span className="text-p-500">Exclude YouTube content from processing</span>
                    <span className="text-secondary text-p-400">
                      Applies to the next sync of this knowledge base
                    </span>
                  </div>
                ),
                checkboxClassName: 'self-start mt-1',
                'data-testid': 'checkbox-ignore-youtube-content',
              },
            },
            {
              name: 'ignore_image_content',
              componentType: 'checkbox',
              zodValidation: z.boolean().optional(),
              inputProps: {
                label: (
                  <div className="flex flex-col">
                    <span className="text-p-500">Exclude image content from processing</span>
                    <span className="text-secondary text-p-400">
                      Applies to the next sync of this knowledge base
                    </span>
                  </div>
                ),
                checkboxClassName: 'self-start mt-1',
                'data-testid': 'checkbox-ignore-image-content',
              },
            },
            {
              name: 'sync_enabled',
              componentType: 'checkbox',
              zodValidation: z.boolean().optional(),
              inputProps: {
                label: 'Automatically sync this knowledge base on a schedule',
                checkboxClassName: 'self-start mt-1',
                'data-testid': 'checkbox-sync-enabled',
              },
            },
            {
              name: 'sync_frequency',
              type: 'custom',
              zodValidation: documentSetupSchema.shape.sync_frequency,
              component: SyncFrequencySelect as unknown as CustomCompType<RenameKBFormValues>,
              inputProps: {
                label: 'Sync data once every',
                placeholder: 'Select sync frequency',
                options: [
                  { value: 'WEEK', label: 'weeks' },
                  { value: 'MONTH', label: 'months' },
                ],
              },
              eventDataExtractor: (data) => ({
                sync_period: data.option,
                sync_frequency: data.value,
              }),
            },
          ]}
          defaultValues={defaultValues}
          onSubmit={(v) =>
            edit.mutate(
              {
                id,
                name: v.name,
                phlo_uuids: v.phlo_uuids,
                sync_frequency: v.sync_frequency.sync_frequency as number,
                sync_period: v.sync_frequency.sync_period as SyncFrequencyPeriod,
                sync_enabled: v.sync_enabled,
                // enrichment prefs are now editable (rag PATCH applies them on the next sync)
                include_image_content: !v.ignore_image_content,
                include_youtube_content: !v.ignore_youtube_content,
              },
              {
                onSuccess: () => {
                  setOpen(false)
                  queryClient.invalidateQueries({ queryKey: flowQueryKeys().knowledgeBases })
                  queryClient.invalidateQueries({ queryKey: flowQueryKeys().knowledgeBase(id) })
                },
              },
            )
          }
          includeSubmitButton={false}
          id="rename-kb-form"
        />
        {linksUnavailable ? (
          <p className="text-secondary text-p-400 mt-2">
            Couldn't load this knowledge base's current agent links. Saving is disabled to avoid
            unlinking them — reopen once they load.
          </p>
        ) : null}
        <DialogFooter>
          <Button onClick={() => setOpen(false)} variant="outline">
            Cancel
          </Button>
          <ButtonWithLoader
            loading={edit.isPending}
            disabled={edit.isPending || linksUnavailable}
            form="rename-kb-form"
            type="submit"
          >
            {edit.isPending ? 'Saving...' : 'Save'}
          </ButtonWithLoader>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
